import { Schema as S } from "effect";

import { CardReadWorkflowResult } from "./workflow.schema";

/**
 * Opaque handle for one server-owned read, available before the vendor returns its log ID.
 *
 * Clients retain this value only for their active flow. They do not parse it or substitute a
 * terminal, patient or CR-log ID; the Bridge resolves its binding and ownership.
 * @since 1.1.11
 * @example
 * const handle = CardReadOperationHandle.make("operation-1");
 */
export const CardReadOperationHandle = S.String.check(
  S.isMinLength(1),
  S.isMaxLength(256),
);

/**
 * Decoded opaque operation handle.
 * @since 1.1.11
 * @example
 * const handle: CardReadOperationHandle = "operation-1";
 */
export type CardReadOperationHandle = typeof CardReadOperationHandle.Type;

/**
 * Request to begin exactly one physical read on the selected terminal.
 * @since 1.1.11
 * @example
 * const request = BeginCardReadRequest.make({ terminalId: "terminal-1" });
 */
export const BeginCardReadRequest = S.Struct({
  terminalId: S.String.check(S.isMinLength(1)),
});

/**
 * Decoded request to begin a card read.
 * @since 1.1.11
 * @example
 * const request: BeginCardReadRequest = { terminalId: "terminal-1" };
 */
export type BeginCardReadRequest = typeof BeginCardReadRequest.Type;

/**
 * Request to observe or abandon the current server-owned operation without repeating a read.
 * @since 1.1.11
 * @example
 * const request = CardReadOperationRequest.make({ operationHandle: "operation-1" });
 */
export const CardReadOperationRequest = S.Struct({
  operationHandle: CardReadOperationHandle,
});

/**
 * Decoded operation lookup or abandonment request.
 * @since 1.1.11
 * @example
 * const request: CardReadOperationRequest = { operationHandle: "operation-1" };
 */
export type CardReadOperationRequest = typeof CardReadOperationRequest.Type;

/**
 * Receipt returned immediately so the client can observe and abandon an unfinished physical read.
 *
 * The timeout is the total operation budget, including physical reading and log resolution; a
 * status request does not restart that budget.
 * @since 1.1.11
 * @example
 * const receipt = CardReadOperationReceipt.make({ operationHandle: "operation-1", timeoutMs: 60000 });
 */
export const CardReadOperationReceipt = S.Struct({
  operationHandle: CardReadOperationHandle,
  timeoutMs: S.Number.check(S.isInt(), S.isGreaterThan(0), S.isLessThanOrEqualTo(60000)),
});

/**
 * Decoded receipt for a started read operation.
 * @since 1.1.11
 * @example
 * const receipt: CardReadOperationReceipt = { operationHandle: "operation-1", timeoutMs: 60000 };
 */
export type CardReadOperationReceipt = typeof CardReadOperationReceipt.Type;

/**
 * Safe reasons a read cannot provide a usable result or confirmed cancellation.
 * @since 1.1.11
 * @example
 * const reason = CardReadUnavailableReason.make("metadata-unavailable");
 */
export const CardReadUnavailableReason = S.Literals([
  "empty-payload",
  "no-patient-card",
  "metadata-unavailable",
  "invalid-result",
  "timeout",
  "operation-unknown",
  "reader-busy",
  "storage-unavailable",
  "processing-failed",
  "validation-failed",
  "cancellation-unconfirmed",
]);

/**
 * Decoded unavailable reason, without patient details or vendor error text.
 * @since 1.1.11
 * @example
 * const reason: CardReadUnavailableReason = "timeout";
 */
export type CardReadUnavailableReason = typeof CardReadUnavailableReason.Type;

/**
 * Current result of observing or abandoning a read operation.
 *
 * Pending may precede the vendor log ID. Ready carries the normalized actionable or completed
 * result; cancelled is reserved for confirmed vendor cancellation. Abandoned means an already
 * completed result was discarded without claiming vendor cancellation.
 * @since 1.1.11
 * @example
 * const status = CardReadOperationStatus.make({ status: "pending" });
 */
export const CardReadOperationStatus = S.Union([
  S.Struct({ status: S.Literal("pending"), crlogId: S.optional(S.String) }),
  S.Struct({ status: S.Literal("ready"), result: CardReadWorkflowResult }),
  S.Struct({ status: S.Literal("cancelled") }),
  S.Struct({ status: S.Literal("abandoned") }),
  S.Struct({ status: S.Literal("unavailable"), reason: CardReadUnavailableReason }),
]);

/**
 * Decoded operation state consumed by the shared card-read UI facade.
 * @since 1.1.11
 * @example
 * const status: CardReadOperationStatus = { status: "pending" };
 */
export type CardReadOperationStatus = typeof CardReadOperationStatus.Type;
